/* eslint-disable max-len */
import React, { PureComponent, Fragment } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import i18n from 'i18next'

import './QuestionLibrary.scss'
import { DragDropContext } from 'react-beautiful-dnd'
import Panel from '../UniformComponents/Panel'
import Badge from '../UniformComponents/Badge'
import Select from '../components/form/Select'
import InfoAccordion from '../components/InfoAccordion'
import Button from '../UniformComponents/Button'
import SearchInput from '../components/SearchInput'
import QuestionItemList from './components/QuestionItemList'
import QuestionBuilder from '../QuestionBuilder'
import QuestionLibraryStore from './QuestionLibraryStore'
import LinkButton from '../UniformComponents/LinkButton'
import DebugControl from '../components/DebugControl'
import QuestionPipe from './components/QuestionPipe'
import HoverInfo from '../UniformComponents/HoverInfo'

const CONTAINER_BOTTOM_MARGIN = 42
const EDIT_PANEL_CLEARANCE = 278
const QUESTION_CONTAINER_CLEARANCE = 411
@observer
class QuestionLibrary extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            showGuideAccordion: false,
            questionHeaderName: '',
            questionCategoryName: '',
        }
        this.questionLibraryStore = new QuestionLibraryStore(props.store, props.screen, Boolean(props.survey))
        if (props.survey) {
            this.initWithSurvey()
        }

        this.leftPanelRef = React.createRef()
        this.scrollableDivRef = React.createRef()
        this.editPanelWrapperRef = React.createRef()
        this.editPanelContentRef = React.createRef()
        this.scrollableWrapperRef = React.createRef()
        this.leftPanelObserver = null
        this.questionListRef = React.createRef()
    }

    async componentDidMount() {
        const {
            store, oid, rid, onLoad, survey, screen,
        } = this.props
        const { questionLibraryStore } = this

        if (screen !== 'surveyBuilder') {
            store.breadcrumps = [{
                url: '/mandatoryquestions/select/',
                title: 'Mandatory Questions',
            },
            {
                url: '',
                title: R.path(['userData', 'clientName'], store),
            }]
        }

        questionLibraryStore.oid = oid
        questionLibraryStore.rid = rid

        window.addEventListener('resize', this.setPanelsHeight)
        this.setPanelsHeight()

        await questionLibraryStore.init()
        questionLibraryStore.loading = false

        if (onLoad) {
            onLoad()
        }

        store.addDropContext('questionLibrary', questionLibraryStore)
        store.addDropContext('qList', questionLibraryStore)

        this.setQuestionCategoryName(oid)
        if (survey) survey.questionLibrary = this
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setPanelsHeight)
    }

    initWithSurvey = () => {
        const { questionLibraryStore } = this
        const { survey } = this.props
        const { metadata } = survey
        const { productInfo, taxonomy } = metadata
        const { surveyType, respondentType } = taxonomy
        const { industrySpecificInfo } = productInfo
        const { productType, industry } = industrySpecificInfo

        questionLibraryStore.industry = {
            value: industry,
        }

        questionLibraryStore.eventType = {
            value: productType,
        }

        questionLibraryStore.surveyType = {
            value: surveyType,
        }

        questionLibraryStore.respondentType = {
            value: respondentType,
        }
        questionLibraryStore.bindQuestionTypes()
        questionLibraryStore.initQuestionTypes()
    }

    setQuestionCategoryName = (oid) => {
        switch (oid) {
            case 3:
                this.setState({
                    questionCategoryName: 'Client',
                })
                break

            case 4:
                this.setState({
                    questionCategoryName: 'Division',
                })
                break

            case 5:
                this.setState({
                    questionCategoryName: 'Portfolio',
                })
                break

            case 8:
                this.setState({
                    questionCategoryName: 'Custom Category',
                })
                break

            default:
                break
        }
    }

    setPanelsHeight = () => {
        this.setQuestionPanelMinHeight()
        this.setQuestionPanelHeight()
    }

    setQuestionPanelMinHeight = () => {
        const screenHeight = window.innerHeight
        const questionContainerElement = this.questionListRef.current
        const questionContainerNewHeight = Math.round(screenHeight) - QUESTION_CONTAINER_CLEARANCE
        if (!questionContainerElement) return
        questionContainerElement.style.minHeight = `${questionContainerNewHeight}px`

        this.setBottomMargins()
    }

    setQuestionPanelHeight = () => {
        const screenHeight = window.innerHeight
        const editPanelWrapperElement = this.editPanelWrapperRef.current
        const editPanelNewHeight = screenHeight - EDIT_PANEL_CLEARANCE
        if (!editPanelWrapperElement) return
        editPanelWrapperElement.style.height = `${editPanelNewHeight}px`

        this.setBottomMargins()
    }

    setBottomMargins = () => {
        const editPanelContentElement = this.editPanelContentRef.current
        editPanelContentElement.style.marginBottom = `${CONTAINER_BOTTOM_MARGIN}px`
    }

    onDragEnd = (result) => {
        const { source, destination } = result
        const { store } = this.props
        const leftPanel = 'qList'
        const rightPanel = 'questionLibrary'

        if (!destination) {
            return
        }
        if (source.droppableId !== destination.droppableId && destination.droppableId === rightPanel) {
            const question = store.dropTargets[source.droppableId].dropRemove(source.index)
            store.dropTargets[destination.droppableId].drop(destination.index, question)
        } else if (destination.droppableId !== leftPanel) {
            store.dropTargets[source.droppableId].dropReorder(source.index, destination.index)
        }
    }

    toggleGuide = () => {
        this.setState((prevState) => ({
            showGuideAccordion: !prevState.showGuideAccordion,
        }))
    }

    setRenderFieldsVisibility = () => {
        const {
            eventType,
            respondentType,
            surveyType,
            addingMaxbi,
        } = this.questionLibraryStore
        const { screen } = this.props

        if (addingMaxbi) {
            return true
        }
        if (!addingMaxbi && eventType.value === 'CE3P' && surveyType.value && respondentType.value) {
            return true
        }
        if (!addingMaxbi && eventType.value !== 'CE3P' && respondentType.value) {
            return true
        }
        if (screen === 'surveyBuilder') return true
        return false
    }

    newQuestion = () => {
        const { store, oid, rid } = this.props
        store.surveyLocale = 'en_GB'
        store.questionDetails = {
            oid,
            productType: this.questionLibraryStore.eventType.value,
            surveyType: 'PE',
            industryType: this.questionLibraryStore.industry.value,
            categoryId: this.questionLibraryStore.questionCategoryId,
            respondentType: this.questionLibraryStore.respondentType.value,
            portfolioId: rid,
            contextOid: oid,
            sgKpis: this.questionLibraryStore.sgKpis,
            addQuestion: this.questionLibraryStore.addQuestion,
        }

        if (this.questionLibraryStore.sgQuestions) {
            store.questionDetails.sgQuestions = this.questionLibraryStore.sgQuestions
        }
        store.showQuestionEditor = true
    }

    renderPageTitle = () => {
        const { store, screen } = this.props
        const isSurveyBuilder = screen === 'surveyBuilder'
        return (
            <Fragment>
                <div className="heading">
                    <h3>{i18n.t('s_q_library')}</h3>
                    {isSurveyBuilder && (
                        <>
                            <HoverInfo
                                cssClass="hover-info question-library"
                                desc={i18n.t('s_q_library_tooltip', { clientName: store.userData?.clientName || '[Your company]' })}
                                dir="right"
                            >
                                <i className="info-icon fa-solid fa-circle-question" />
                            </HoverInfo>
                            <DebugControl store={store} />
                        </>
                    )}
                </div>
            </Fragment>
        )
    }

    renderGuideContent = () => {
        const { questionLibraryStore, props: { guideAccordionContent } } = this

        if (guideAccordionContent) {
            return (
                <div className="guide-content-wrapper">
                    {guideAccordionContent}
                </div>
            )
        }

        return (
            <div className="guide-content-wrapper">
                {questionLibraryStore.guideAccordionContent.map((item) => (
                    <div className="guide-content-item">
                        <p className="guide-content-item-header">
                            {item.title}
                        </p>
                        <p className="guide-content-item-paragraph">
                            {item.content}
                        </p>
                    </div>
                ))}
            </div>
        )
    }

    renderGuideAccordion = () => {
        const { showGuideAccordion } = this.state

        return (
            <Fragment>
                <div className="guide-accordion-container">
                    <InfoAccordion
                        title={i18n.t('searchLayout.getStartGuide')}
                        show={showGuideAccordion}
                        toggle={this.toggleGuide}
                        infoContentClass="mock-questions-guide"
                    >
                        {this.renderGuideContent()}
                    </InfoAccordion>
                </div>
            </Fragment>
        )
    }

    renderFilters = () => {
        const {
            industries,
            industry,
            isIndustryVisible,
            eventTypes,
            eventType,
            eventTypeShowDisabled,
            isEventTypeVisible,
            surveyTypes,
            surveyType,
            isSurveyTypeVisible,
            isRespondentTypeVisible,
            respondentTypes,
            respondentType,
            respondentTypeShowDisabled,
            handleIndustry,
            handleEventType,
            handleSurveyType,
            handleRespondentType,
        } = this.questionLibraryStore

        const availableFilters = [
            {
                name: i18n.t('eventIndustry', 'Event industry'),
                element: () => (
                    <Select
                        label={i18n.t('eventIndustry', 'Event industry')}
                        options={industries}
                        onBlur={handleIndustry}
                        value={industry}
                    />
                ),
                show: isIndustryVisible,
            },
            {
                name: i18n.t('product_type', 'Event type'),
                element: () => (
                    <Select
                        label={i18n.t('product_type', 'Event type')}
                        options={eventTypes}
                        onBlur={handleEventType}
                        value={eventType}
                        showDisabled={eventTypeShowDisabled}
                    />
                ),
                show: isEventTypeVisible,
            },
            {
                name: i18n.t('survey_type', 'Survey type'),
                element: () => (
                    <Select
                        label={i18n.t('survey_type', 'Survey type')}
                        options={surveyTypes}
                        onBlur={handleSurveyType}
                        value={surveyType}
                    />
                ),
                show: isSurveyTypeVisible,
            },
            {
                name: i18n.t('respondent_type', 'Respondent type'),
                element: () => (
                    <Select
                        label={i18n.t('respondent_type', 'Respondent type')}
                        options={respondentTypes}
                        onBlur={handleRespondentType}
                        value={respondentType}
                        showDisabled={respondentTypeShowDisabled}
                    />
                ),
                show: isEventTypeVisible && isRespondentTypeVisible,
            },
        ]

        const allowedFilters = availableFilters.map((filter) => {
            if (filter.show) return (<div className="filter-item">{filter.element()}</div>)
            return null
        })

        if (allowedFilters.length > 0) return (<Fragment>{allowedFilters}</Fragment>)
        return null
    }

    renderContent = () => {
        const { questionLibraryStore, questionListRef } = this
        const { store, allowInherit } = this.props

        return (
            <QuestionItemList
                dropContext="questionLibrary"
                appStore={store}
                store={questionLibraryStore}
                isFullSize
                innerRef={questionListRef}
                allowInherit={allowInherit}
            />
        )
    }

    renderFields = () => {
        const { screen } = this.props
        const {
            questionTypesList,
            questionType,
            categoryTypes,
            categoryType,
            handleCategoryType,
            handleQuestionType,
        } = this.questionLibraryStore

        const { questionLibraryStore } = this
        const scrollStyles = { position: 'relative' }

        const availableFields = [
            {
                element: () => (
                    <SearchInput
                        label={i18n.t('search')}
                        ref={(s) => { questionLibraryStore.qSearch = s }}
                        onBlur={() => questionLibraryStore.filterQuestionLibraryList(questionType.value)}
                        cssClass="question-library-search"
                    />
                ),
                show: true,
            },
            {
                element: () => (
                    <Select
                        label={i18n.t('questionLibrary.searchPanel.questionList')}
                        options={questionTypesList}
                        onBlur={handleQuestionType}
                        value={questionType}
                        style={scrollStyles}
                        cssClass="question-library-dropdown"
                    />
                ),
                show: true,
            },
            {
                element: () => (
                    <Select
                        label={i18n.t('category')}
                        options={categoryTypes}
                        onBlur={handleCategoryType}
                        value={categoryType}
                        style={scrollStyles}
                        cssClass="question-library-dropdown"
                    />
                ),
                show: questionType.value === 'explori',
            },
        ]

        const allowedFields = availableFields.map((field) => (
            field.show ? (<Fragment>{field.element()}</Fragment>) : null
        ))

        return allowedFields.length > 0 ? (
            <Fragment>
                {screen !== 'surveyBuilder'
                    ? (
                        <div className="create-question-button-wrapper">
                            <Button
                                cssClass="button primary large w-100"
                                icon="fa-light fa-circle-plus"
                                label={i18n.t('questionLibrary.searchPanel.createNewQuestion')}
                                onClick={this.newQuestion}
                            />
                        </div>
                    ) : null}

                <div className="question-filters-wrapper">
                    {allowedFields}
                </div>
            </Fragment>
        ) : null
    }

    renderFilterPanel = () => {
        const { questionHeaderName, questionCategoryName } = this.state
        return (
            <Panel cssClass="filter-panel overflow-visible">
                <div className="panel-header">
                    <div className="left">
                        <div className="header-title">
                            <h3>{i18n.t('questionList')}</h3>
                            <h3 className="question-title">{questionHeaderName}</h3>
                        </div>
                    </div>
                    <div className="right">
                        {questionCategoryName && (
                            <Badge
                                type="success"
                                cssClass="uc-badge success status-badge"
                                label={questionCategoryName}
                                style={{ width: 'auto', height: '28.5px', minWidth: '108px' }}
                            />
                        )}
                        <LinkButton
                            cssClass="button primary regular"
                            icon="fa-sharp fa-regular fa-arrow-left"
                            label={i18n.t('back')}
                            href="/mandatoryquestions/select/"
                            customClass="back-button"
                        />
                    </div>
                </div>
                <div className="panel-content">
                    {this.renderFilters()}
                </div>
            </Panel>
        )
    }

    renderQuestionLibraryList = () => {
        const {
            respondentType,
            surveyType,
        } = this.questionLibraryStore
        const { store, survey } = this.props
        const { questionLibraryStore } = this

        const questionIdsInSurvey = new Set(survey?.pages?.flatMap(
            (page) => (page.questions
                ? page.questions.map((q) => q.questionId)
                : []
            ),
        ))

        return (
            <div className="filtered-question-list">
                {respondentType.value || surveyType.value ? (
                    <QuestionItemList
                        dropContext="qList"
                        appStore={store}
                        store={questionLibraryStore}
                        questionIds={questionIdsInSurvey}
                        isQuestionLibrary
                    />
                ) : ''}
            </div>
        )
    }

    render() {
        const {
            loading,
            filteredList,
            questionType,
            questionPipeStore,
        } = this.questionLibraryStore
        const { store, screen, show } = this.props

        if (filteredList.length > 0) this.setQuestionPanelHeight()

        if (screen === 'surveyBuilder' && (loading || show === false)) return null

        const libraryContent = (
            <Fragment>
                <div id="qList" ref={this.leftPanelRef}>
                    <div className="left-container">
                        <div className="scrollbar-mask" />
                        <Panel cssClass="search-panel" style={{ overflow: 'visible' }}>
                            {this.renderPageTitle()}
                            <div className="scrollable-content" style={{ overflowY: questionType.value === '' ? 'visible' : 'scroll' }}>
                                <div className="scrollable-wrapper" ref={this.scrollableWrapperRef}>
                                    {this.renderGuideAccordion()}
                                    {this.setRenderFieldsVisibility() ? this.renderFields() : null}
                                    {this.renderQuestionLibraryList()}
                                </div>
                            </div>
                        </Panel>
                    </div>
                </div>

                {screen !== 'surveyBuilder'
                    ? (
                        <div className="right-container">
                            {this.renderFilterPanel()}
                            <div className="edit-panel-wrapper" ref={this.editPanelWrapperRef}>
                                <Panel cssClass="edit-panel overflow-visible" innerRef={this.editPanelContentRef}>
                                    {this.renderContent()}
                                </Panel>
                            </div>
                        </div>
                    ) : null}

                {screen !== 'surveyBuilder' && store.showQuestionEditor && (
                    <QuestionBuilder store={store} />
                )}
            </Fragment>
        )

        return (
            <div id="questionLibrary">
                <div className="question-library-container">
                    {screen !== 'surveyBuilder'
                        ? (
                            <DragDropContext onDragEnd={this.onDragEnd}>
                                {libraryContent}
                            </DragDropContext>
                        ) : libraryContent}
                </div>

                <div className={store.survey.closed ? 'question-library-cover' : ''} />

                <QuestionPipe store={questionPipeStore} />
            </div>
        )
    }
}

export default QuestionLibrary
